import { Component, EnvironmentInjector, inject, Input, OnInit, runInInjectionContext, signal, Signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  BalanceQuery,
  CartV2Service,
  config,
  DISABLED_STATUSES,
  ErrorStateService,
  LoyaltyCategoryType,
  REWARD_STATUS,
  MarketplaceFacade,
  MarketplaceCustomFieldModel,
  MarketplaceItemLimitType,
  MarketplaceItemMappingAttribute,
  MarketplaceItemV2,
  MarketplaceV2Query,
  ScreenType,
  TermsAndCondtionsType,
  MarketplaceItemAttributeValue,
  CustomFieldValidationType,
} from '@fgb/core';
import { map, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { MarketplaceButtonAction } from './action-buttons/marketplace-button.action';
import { resolveMarketplaceAction } from './action-buttons/marketplace-action-resolver';
import { isMerchantItem } from './action-buttons/marketplace-action.rules';
import { CommonModule } from '@angular/common';
import { ShortLongDescriptionModule } from '@fgb/portal-component-library/src/lib/shared/short-long-description';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { QuantityFieldComponent } from '@fgb/portal-component-library/src/lib/shared/quantity-field';
import { CountdownV2Component } from '@fgb/portal-component-library/src/lib/shared/countdown-v2';
import { AttributeFieldComponent } from './attribute-field/attribute-field.component';
import { SocialShareModalModule } from '@fgb/portal-component-library/src/lib/shared/social-share-modal';
import { CountdownModule } from '@fgb/portal-component-library/src/lib/shared/countdown';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { CustomFieldComponent } from '@fgb/portal-component-library/src/lib/shared/custom-field';

interface MarketplaceDetailContext {
  item: MarketplaceItemV2;
  isDisabled: boolean;
  isUnlimited: boolean;
  isMerchantItem: boolean;
  isEcashItem: boolean;
  isAttributeItem: boolean;
  isLimitedItem: boolean;
  isFormDisabled: boolean;
  stock: number;
  action: MarketplaceButtonAction | null;
}

@Component({
  selector: 'fgbcl-marketplace-details-v2',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    LoadingModule,
    ShortLongDescriptionModule,
    DirectivesModule,
    FormsModule,
    ReactiveFormsModule,
    FGBSharedModule,
    QuantityFieldComponent,
    CountdownV2Component,
    AttributeFieldComponent,
    SocialShareModalModule,
    CountdownModule,
    ErrorsModule,
    CustomFieldComponent,
  ],
  templateUrl: './marketplace-details-v2.component.html',
  styleUrl: './marketplace-details-v2.component.scss',
})
export class MarketplaceDetailsV2Component implements OnInit {
  @Input() maxAllowedQuantity = 100; // This is an arbitrary cap for maximum quantity when stock is unlimited. Adjust as necessary.
  @Input() itemId: number;
  @Input() completeRedirect: string = '/rewards/marketplace';
  private readonly marketplaceFacade = inject(MarketplaceFacade);
  private readonly marketplaceQuery = inject(MarketplaceV2Query);
  private readonly cartService = inject(CartV2Service);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly balanceQuery = inject(BalanceQuery);
  private readonly injector = inject(EnvironmentInjector);
  private readonly errorStateService = inject(ErrorStateService);

  /** Stream of the resolved item context. Emits `null` if the item is not found in the store. */
  context$!: Observable<MarketplaceDetailContext | null>;
  /** Reactive form managing quantity, attribute selection, custom fields, and terms acceptance. */
  marketplaceDetailForm: FormGroup;
  /** Maximum purchasable quantity, derived from stock, funds, and applicable limits. */
  maxQuantity = signal<number>(0);
  /** Minimum purchasable quantity. Defaults to 1. */
  minQuantity = signal<number>(1);
  marketplaceStatus = REWARD_STATUS;
  TermsAndCondtionsType = TermsAndCondtionsType;
  loyaltyCategoryType = LoyaltyCategoryType.Marketplace;
  screenSize = ScreenType;
  selectedAttribute: MarketplaceItemAttributeValue;

  /** Whether the redeem button is disabled: the quantity is disabled, or it exceeds the item limit or the maximum quantity. */
  get isRedeemDisabled(): boolean {
    const errors = this.marketplaceDetailForm?.errors;
    return (
      !!this.marketplaceDetailForm?.get('quantity')?.disabled ||
      !!errors?.['limitExceeded'] ||
      !!errors?.['productLimitExceeded'] ||
      !!errors?.['itemLimitExceeded'] ||
      !!errors?.['maxQuantityExceeded']
    );
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  /** Clears any stale errors and initialises the item context stream from the store. */
  ngOnInit(): void {
    this.errorStateService.clearErrors(); // clear errors just in case api error triggered outside of this component
    this.context$ = this.marketplaceQuery
      .selectMarketplaceById()
      .pipe(map((marketplaceItem) => this.buildContext(marketplaceItem)));
  }

  /* ================================
   * Public API
   * ================================ */

  /** Handles changes to the selected attribute. Enables quantity input if applicable. */
  onAttributeChange(ctx: MarketplaceDetailContext, attributeSelection: MarketplaceItemAttributeValue): void {
    if (!attributeSelection) return;
    this.selectedAttribute = attributeSelection;

    ctx.isUnlimited = attributeSelection.UnlimitedStock ?? false;
    ctx.stock = attributeSelection.AvailableStockCount || 0;

    const acceptTermsControl = this.marketplaceDetailForm.get('acceptTerms');
    const hasTermsToggle = !!acceptTermsControl?.enabled;

    if (hasTermsToggle) {
      this.onAcceptTermsChange(!!acceptTermsControl?.value);
    } else if (this.marketplaceDetailForm.get('quantity')?.disabled) {
      this.marketplaceDetailForm.get('quantity')?.enable({ emitEvent: false });
    }

    this.resetQuantity();
    this.assignMaxQuantity(ctx);
  }

  /** Handles changes to the acceptance of terms and conditions. Enables or disables the quantity input based on the acceptance state. */
  onAcceptTermsChange(acceptTerms: boolean): void {
    const quantityControl = this.marketplaceDetailForm.get('quantity');
    const attributeControl = this.marketplaceDetailForm.get('attribute');

    if (!quantityControl) return;
    const hasSelectedAttribute = !attributeControl?.enabled || !!attributeControl?.value;

    if ((!attributeControl?.enabled && acceptTerms) || (acceptTerms && hasSelectedAttribute)) {
      quantityControl.enable({ emitEvent: false });
    } else {
      quantityControl.disable({ emitEvent: false });
    }
  }

  /** On complete update the store status to none or available */
  onComingSoonComplete(): void {
    this.marketplaceFacade.updateMarketplaceStatus(this.itemId, REWARD_STATUS.None);
  }

  /** On marketplace end, navigate to the complete redirect page. */
  onMarketplaceEnd(): void {
    this.router.navigate([this.completeRedirect]);
  }

  /**
   * Validates the form and delegates execution to a dynamically resolved action strategy.
   *
   * **Why this separation matters (Strategy Pattern):**
   * Adding support for a new item type (e.g. a charity donation item) only requires:
   *   1. A new `MarketplaceButtonAction` subclass with its own `invoke()` logic
   *   2. A new rule added to `actionRules`
   * This component stays completely unchanged. Without this pattern, every new item
   * type would require a new `if/else` branch here, growing this method indefinitely.
   *
   */
  invokeAction(item: MarketplaceItemV2, action: MarketplaceButtonAction): void {
    this.marketplaceDetailForm.markAllAsTouched();
    this.errorStateService.clearErrors();
    if (this.marketplaceDetailForm.valid) {
      const { quantity, customFields } = this.marketplaceDetailForm.value;
      if (action) {
        // Map custom fields to the expected format for the enterLotto method, ensuring checkbox values are strings before submission.
        // Api expects checkbox values to be strings, so we convert them accordingly.
        const mappedCustomFields = customFields?.map((field: any) => ({
          ...field,
          customFieldValue:
            field.customValidationType === CustomFieldValidationType.Checkbox
              ? `${field.customFieldValue}`
              : field.customFieldValue,
        }));

        runInInjectionContext(this.injector, () => {
          action.invoke({ item, quantity, attribute: this.selectedAttribute, customFields: mappedCustomFields });
        });
      }
    }
  }

  /** Validates the form and adds the selected item and quantity to the cart. */
  addProductToCart(ctx: MarketplaceDetailContext): void {
    this.marketplaceDetailForm.markAllAsTouched();
    this.errorStateService.clearErrors();

    if (this.marketplaceDetailForm.valid) {
      const { quantity, customFields } = this.marketplaceDetailForm.value;

      this.cartService.addItemToCart(ctx.item, quantity, this.selectedAttribute, customFields);
    }
  }

  /** Resets the quantity field to its default value. */
  resetQuantity(): void {
    this.marketplaceDetailForm.get('quantity')?.patchValue(this.minQuantity());
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Builds the context object and initializes related properties for the given marketplace item. */
  private buildContext(item: MarketplaceItemV2 | null) {
    if (!item) return null;
    const context = this.createContext(item);
    this.resolveStock(context);
    this.assignMaxQuantity(context);
    this.applyFormDisabledState(context);
    this._initializeForm(context);
    this.assignActionButton(context);
    return context;
  }

  /** Creates a context object for the given marketplace item. */
  private createContext(item: MarketplaceItemV2) {
    return {
      item,
      isDisabled: this.getDisabledState(item) || item.DisableManualEntry,
      isUnlimited: !item?.StockCount,
      isMerchantItem: isMerchantItem(item),
      isEcashItem: item?.EcashValue > 0,
      isAttributeItem: item?.Attributes && item.Attributes.length > 0,
      isLimitedItem: this.getLimitedState(item),
      isFormDisabled: false,
      stock: item?.StockCount || 0,
      action: null as MarketplaceButtonAction | null,
    };
  }

  /** Gets the disabled state of the item based on its status. */
  private getDisabledState(item: MarketplaceItemV2): boolean {
    return DISABLED_STATUSES.has(item?.Status);
  }

  /** Gets the limited state of the item based on its limit type and count. */
  private getLimitedState(item: MarketplaceItemV2): boolean {
    return (
      (item.LimitType !== MarketplaceItemLimitType.NoLimit && item.LimitCount > 0) ||
      (item.ItemLimitType !== MarketplaceItemLimitType.NoLimit && item.ItemLimitCount > 0)
    );
  }

  /** Determines the maximum allowable quantity based on stock availability and applicable business constraints. */
  private assignMaxQuantity(ctx: ReturnType<typeof this.createContext>): void {
    if (ctx.isMerchantItem) {
      this.maxQuantity.set(ctx.isUnlimited ? this.maxAllowedQuantity : ctx.stock);
      return;
    }

    const quantityLimitFromFunds = this.getQuantityLimitFromFunds(ctx);
    const capAmount = ctx.isUnlimited ? this.maxAllowedQuantity : ctx.stock;
    this.maxQuantity.set(Math.min(quantityLimitFromFunds, capAmount));
  }

  /** For attribute items, replaces the top-level stock count with the sum across all attribute mappings. */
  private resolveStock(ctx: ReturnType<typeof this.createContext>): void {
    if (ctx.isAttributeItem) {
      ctx.stock = this.calculateAttributeStockAvailability(ctx.item.AttributeMappings);
    }
  }

  /** Resolves and assigns the correct action button for the item. Sets `null` if the item is disabled or no action matches. */
  private assignActionButton(ctx: ReturnType<typeof this.createContext>): void {
    if (ctx.isDisabled) {
      ctx.action = null;
      return;
    }

    const actionButton = resolveMarketplaceAction(ctx.item);
    if (!actionButton) {
      ctx.action = null;
      return;
    }

    ctx.action = actionButton.create();
    ctx.action.label = actionButton.label;
  }

  /** Sums available stock across all attribute mappings that have finite stock. Unlimited mappings are excluded from the total. */
  private calculateAttributeStockAvailability(attributeMappings: MarketplaceItemMappingAttribute[]) {
    return attributeMappings
      .filter((mapping) => !mapping.UnlimitedStock)
      .reduce((total, mapping) => total + (mapping.AvailableStockCount || 0), 0);
  }

  /** Applies the disabled state to the form based on item attributes, terms, and quantity constraints. */
  private applyFormDisabledState(ctx: ReturnType<typeof this.createContext>): void {
    if (ctx.isLimitedItem) {
      const limitStockCount =
        ctx.item.LimitType !== MarketplaceItemLimitType.NoLimit && ctx.item.LimitCount > 0
          ? ctx.item.LimitCount - ctx.item.AccountPurchaseCount
          : ctx.item.ItemLimitType !== MarketplaceItemLimitType.NoLimit && ctx.item.ItemLimitCount > 0
            ? ctx.item.ItemLimitCount - ctx.item.MemberPurchaseCount
            : 0;
      if (limitStockCount <= 0) {
        ctx.isFormDisabled = true;
        return;
      }
    }
    // Disable form if it's an attribute item without a selected attribute, if it's an item with terms & conditions, or if the max quantity is 0 or less
    ctx.isFormDisabled =
      !!(ctx.isAttributeItem || ctx.item.ShowTermsAndConditions) ||
      this.maxQuantity() <= 0 ||
      (!ctx.isUnlimited && ctx.stock <= 0);
  }

  /** Initializes the main form for the marketplace detail view. */
  private _initializeForm(ctx: ReturnType<typeof this.createContext>): void {
    const isQuantityDisabled = ctx.isFormDisabled;
    const isTermsDisabled = !ctx.item.ShowTermsAndConditions; // disable terms acceptance if the item does not have terms and conditions
    this.marketplaceDetailForm = this.fb.group(
      {
        attribute: [{ value: null, disabled: !ctx.isAttributeItem }, Validators.compose([Validators.required])],
        quantity: [{ value: 1, disabled: isQuantityDisabled }, Validators.compose([Validators.required, Validators.min(1)])],
        customFields: this.fb.array(
          ctx.item.CustomFields
            ? ctx.item.CustomFields.map((field: MarketplaceCustomFieldModel) => this._initializeCustomFieldForm(field))
            : []
        ),
        acceptTerms: [{ value: false, disabled: isTermsDisabled }, Validators.compose([Validators.requiredTrue])],
      },
      {
        validators: [maxQuantityValidator(this.maxQuantity, ctx), qtyMembershipLimitValidator(ctx)],
      }
    );
  }

  /** Initializes a form group for a custom field. */
  private _initializeCustomFieldForm(field: MarketplaceCustomFieldModel): FormGroup {
    let validators = [];
    const isCheckbox = field.FieldType === 2;

    if (field.IsRequired) {
      validators.push(isCheckbox ? Validators.requiredTrue : Validators.required);
    }
    if (field.CharacterLimit > 0) {
      validators.push(Validators.maxLength(field.CharacterLimit));
    }
    if (field.ValidationRegEx != '') {
      validators.push(Validators.pattern(field.ValidationRegEx));
    }
    return this.fb.group({
      customFieldId: [field.CustomFieldId],
      customFieldName: [field.DisplayName],
      customFieldValue: [isCheckbox ? false : '', validators],
    });
  }

  /** Calculates the maximum redeemable quantity based on the user's available balance and item unit value. */
  private getQuantityLimitFromFunds(ctx: ReturnType<typeof this.createContext>): number {
    let balance = this.balanceQuery.getPurse(config.purseConfig.virtualLoyalty);
    const value = ctx.isEcashItem ? ctx.item.EcashValue / 100 : ctx.item.PointValue;
    if (ctx.isEcashItem) {
      balance = this.balanceQuery.getPurse(config.purseConfig.combined);
    }
    if (!value || Number.isNaN(value)) return Number.MAX_SAFE_INTEGER;

    return Math.max(0, Math.floor(balance / value));
  }
}

/* ================================
 * Form Validators
 * ================================ */

/** Validates that the quantity does not exceed the membership limit. */
function qtyMembershipLimitValidator(ctx: MarketplaceDetailContext) {
  return (group: AbstractControl): ValidationErrors | null => {
    const quantity = group.get('quantity')?.value;
    // Product limit validation
    if (ctx.item.LimitType !== MarketplaceItemLimitType.NoLimit && ctx.item.LimitCount > 0) {
      const maxQuantity =
        ctx.item.LimitType === MarketplaceItemLimitType.LimitByMember
          ? ctx.item.LimitCount - ctx.item.MemberPurchaseCount
          : ctx.item.LimitCount - ctx.item.AccountPurchaseCount;
      if (maxQuantity <= 0) {
        return { limitExceeded: true };
      }
      return quantity > maxQuantity && maxQuantity > 0
        ? { productLimitExceeded: { limit: maxQuantity, actual: quantity, type: ctx.item.LimitType } }
        : null;
    }
    // Item limit validation
    if (ctx.item.ItemLimitType !== MarketplaceItemLimitType.NoLimit && ctx.item.ItemLimitCount > 0) {
      const maxQuantity =
        ctx.item.ItemLimitType === MarketplaceItemLimitType.LimitByMember
          ? ctx.item.ItemLimitCount - ctx.item.MemberPurchaseCount
          : ctx.item.ItemLimitCount - ctx.item.AccountPurchaseCount;
      if (maxQuantity <= 0) {
        return { limitExceeded: true };
      }
      return quantity > maxQuantity && maxQuantity > 0
        ? { itemLimitExceeded: { limit: maxQuantity, actual: quantity, type: ctx.item.ItemLimitType } }
        : null;
    }
    return null;
  };
}

/** Validates that the selected quantity does not exceed the maximum allowed quantity. A maximum of 0 may indicate insufficient user balance to redeem the item. */
function maxQuantityValidator(maxQuantity: Signal<number>, ctx: MarketplaceDetailContext) {
  return (group: AbstractControl): ValidationErrors | null => {
    const quantity = group.get('quantity')?.value;
    if (ctx.isUnlimited || group.get('quantity')?.disabled) {
      return null; // skip max quantity validation if stock is unlimited, as the max quantity is determined by user funds in this case
    }
    return quantity > maxQuantity() ? { maxQuantityExceeded: { max: maxQuantity(), actual: quantity } } : null;
  };
}
